import { PendingBulkApprovalActions } from "../BulkApprovalBar";
import { usePendingBulkApproval } from "../usePendingBulkApproval";
import { buildBreakApproval } from "../bulkApprovalRequests";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { StatusRow } from "components/CustomComponents/StatusRow";
import ApprovalAuditTrail from "components/DesignSystem/ApprovalAuditTrail";
import {
  scrollTop,
  getStatusFlow,
  StatusBadge,
  displayFlexItem,
} from "services/constants";
import { Apply } from "./Apply";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { Box } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { TeamZoneBreak } from "../Headers";
import { BreakDetailPanel } from "./BreakDetailPanel";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import { BreakStatusCards, canonicalBreakStatus, breakStatusVisual } from "./BreakStatusCards";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableHeadCell,
  approveTableBodyCell,
  bulkSelectionCheckboxSx,
  NewRequestButton,
  ApproveTable,
} from "../approveUi";
import moment from "moment";
import { resolveRangeLabel } from "../dateRangeUtils";
import { stripEmployeeIdFromName } from "../displayNameUtils";

// Same "Default View" sentinel pattern as the other split screens.
const BREAK_LIST_DEFAULT_VIEW_ID = "__default_break_list_view__";

// Was a locally re-implemented copy of the same name-cleaning idea, but its
// fallback (no explicit id passed) only stripped a trailing run of digits —
// it left a letter-prefixed employee code (e.g. "EMP045") stuck straight
// onto the name with no space. approved_by/rejected_by/cancelled_by below
// never had a per-action id to pass in, so they always hit that weak
// fallback. displayNameUtils' stripEmployeeIdFromName (used by the other
// Approve split screens) handles a letter-prefixed code correctly.
const cleanBreakDisplayName = stripEmployeeIdFromName;

// The "Date" column already carries the date — these are only ever used for
// break_start_time/break_end_time, so showing the date again here just
// crowded the column with no extra information.
const formatBreakListTime = (value) => {
  if (!value) return value;
  const plainTime = moment(value, ["HH:mm", "H:mm"], true);
  if (plainTime.isValid()) return plainTime.format("HH:mm");

  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("HH:mm") : value;
};

const formatBreakCategory = (value) => {
  if (value === "Shift_break") return "Shift Break";
  if (value === "Ot_break" || value === "OT_break") return "OT Break";
  return value;
};

const normalizeBreakRequestRow = (row) => {
  const employeeName = cleanBreakDisplayName(
    row?.employee_name || row?.username || row?.user_name,
    row?.employee_id || row?.employee_code,
  );
  const approvedBy = cleanBreakDisplayName(row?.approved_by);
  const rejectedBy = cleanBreakDisplayName(row?.rejected_by);
  const cancelledBy = cleanBreakDisplayName(row?.cancelled_by);
  const attendanceType =
    row?.attendance_type === "Break" ? "Apply Break" : row?.attendance_type;

  return {
    ...row,
    attendance_type: attendanceType,
    raw_attendance_type: row?.raw_attendance_type || row?.attendance_type,
    employee_name: employeeName || row?.employee_name,
    applied_by: cleanBreakDisplayName(
      row?.applied_by || employeeName,
      row?.employee_id || row?.employee_code,
    ),
    approved_by: approvedBy,
    rejected_by: rejectedBy,
    cancelled_by: cancelledBy,
    approver_name: approvedBy || rejectedBy || cancelledBy || "-",
    break_type: formatBreakCategory(row?.break_type),
    raw_break_type: row?.raw_break_type || row?.break_type,
    raw_break_start_time: row?.raw_break_start_time || row?.break_start_time,
    raw_break_end_time: row?.raw_break_end_time || row?.break_end_time,
    break_start_time: formatBreakListTime(row?.break_start_time),
    break_end_time: formatBreakListTime(row?.break_end_time),
    breaks: Array.isArray(row?.breaks)
      ? row.breaks.map((item) => ({
          ...item,
          raw_break_start_time:
            item?.raw_break_start_time ||
            item?.break_start_time ||
            item?.start_time,
          raw_break_end_time:
            item?.raw_break_end_time || item?.break_end_time || item?.end_time,
          break_start_time: formatBreakListTime(item?.break_start_time),
          break_end_time: formatBreakListTime(item?.break_end_time),
          start_time: formatBreakListTime(item?.start_time),
          end_time: formatBreakListTime(item?.end_time),
        }))
      : row?.breaks,
  };
};

export const Break = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel: dateRangeLabelProp,
  handleStartDate,
  subModuleId,
}) => {
  const {
    domain,
    userDetails,
    topbarCompanyId,
    topbarCompanyName,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const containerRef = useRef(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [breakData, setBreakData] = useState([]);
  const [breakFilterData, setBreakFilterData] = useState([]);
  const [viewBreakDetails, setViewBreakDetails] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  // Initialized from (and kept in sync with) the Approval Center landing
  // page's own dateRangeLabel prop — previously hardcoded to "Last 7 Days"
  // regardless of the actual startDate/endDate this screen was handed, on
  // a fresh ?view= deep link or Team Workspace Home's "date" param. A
  // manual re-pick on this screen's own picker still overrides it locally.
  const [dateRangeLabel, setDateRangeLabel] = useState(
    dateRangeLabelProp || "Last 7 Days",
  );

  useEffect(() => {
    if (dateRangeLabelProp) setDateRangeLabel(dateRangeLabelProp);
  }, [dateRangeLabelProp]);
  const {
    expanded: breakPanelExpanded,
    toggleExpanded: toggleBreakPanelExpanded,
  } = useExpandablePanel(viewBreakDetails);
  const {
    expanded: createBreakPanelExpanded,
    toggleExpanded: toggleCreateBreakPanelExpanded,
  } = useExpandablePanel(isCreatePanelOpen);

  useEffect(() => {
    const fetchBreakDetails = async () => {
      const res = await customGetAPI(
        `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}&start_date=${startDate}&end_date=${endDate}`,
      );
      const rows = Array.isArray(res?.responseDetails)
        ? res.responseDetails
        : (res?.responseDetails?.results ?? []);
      const mappedRows = rows
        .filter(
          (row) =>
            row?.attendance_type === "Break" ||
            row?.attendance_type === "Cancel Break",
        )
        .map((row) => ({
          ...row,
          // getStatusFlow (services/constants) already reads these same
          // fields for the expand row's approval timeline — one column
          // showing whichever of the three actually fired for this row.
          approver_name:
            row.approved_by || row.rejected_by || row.cancelled_by || "-",
        }))
        .map(normalizeBreakRequestRow);
      setBreakData(mappedRows);
      // Also seed the filtered view directly — CustomFilter recomputes it
      // in its own effect a render later, so without this the table
      // flashes "No Matching Results" for a frame right as loading ends.
      setBreakFilterData(mappedRows);
      setLoading(false);
      setReCallApi(false);
    };
    if (startDate && endDate && reCallApi) {
      setLoading(true);
      fetchBreakDetails();
    }
  }, [
    domain,
    endDate,
    reCallApi,
    setReCallApi,
    startDate,
    topbarCompanyId,
    userDetails?.id,
  ]);

  // Checks its own "Break" submodule rather than sharing WorkAdjustment's
  // bucket — the backend doesn't provision this submodule yet, so this
  // reads as no access until it's added there.
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Approvals",
    "Break",
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (!footerNode) {
      setFooterHeight(0);
      return;
    }
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setBreakFilterData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  // Tallies for the Pending/Approved/Rejected cards, computed from the full
  // date-range fetch (breakData) rather than breakFilterData — so the
  // counts stay stable while the user searches/sorts via CustomFilter.
  // Aggregates both attendance_type values together.
  const breakStatusCounts = useMemo(
    () =>
      (breakFilterData || []).reduce((acc, request) => {
        const status = canonicalBreakStatus(request?.status);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [breakFilterData],
  );

  const visibleBreakData = useMemo(() => {
    if (!statusCardFilter) return breakFilterData;
    return breakFilterData.filter(
      (request) => canonicalBreakStatus(request?.status) === statusCardFilter,
    );
  }, [breakFilterData, statusCardFilter]);

  const bulkApproval = usePendingBulkApproval({
    rows: visibleBreakData,
    canonicalStatus: canonicalBreakStatus,
    canApprove: check_access && statusCardFilter === "Pending",
    approveRow: (row) => customAPI(
      `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${topbarCompanyId}`,
      "PUT",
      buildBreakApproval(row),
    ),
    rejectRow: (row, reason) => customAPI(
      `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${topbarCompanyId}`,
      "PUT",
      buildBreakApproval(row, "Rejected", reason),
    ),
    onApproved: () => setReCallApi(true),
  });

  const handleStatusCardClick = (key) => {
    setPage(0);
    setExpandedIndex(null);
    setExpandedData(null);
    setViewBreakDetails(false);
    setSelectedData(null);
    if (key === "All") {
      setStatusCardFilter("");
    } else {
      setStatusCardFilter(key);
    }
  };

  const handleBreakDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const breakColumnHeaders = useMemo(
    () => TeamZoneBreak.filter((header) => header.key !== "expand"),
    [],
  );

  // Default View / saved-view selector — same shape as the other split
  // screens. Its own storageKey namespaces this screen's saved views
  // separately from WorkAdjustment's, even though both share subModuleId.
  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_break_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: BREAK_LIST_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId = activeViewId || BREAK_LIST_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== BREAK_LIST_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(
    () => breakColumnHeaders,
    [breakColumnHeaders],
  );

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find(
        (view) => view.id === BREAK_LIST_DEFAULT_VIEW_ID,
      ) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter(
        (view) => view.id !== BREAK_LIST_DEFAULT_VIEW_ID,
      ),
    [savedViewsForScope],
  );

  const defaultView = useMemo(() => {
    if (storedDefaultView?.isCustomDefault) {
      // Whatever headerConfig the user saved is authoritative — same as any
      // other saved view. (Previously this back-filled any header missing
      // from headerConfig, meant to surface columns added to the schema
      // after the view was saved — but that's indistinguishable from a
      // column the user deliberately unchecked, so it silently re-added
      // every unchecked header right back on the next render.)
      return storedDefaultView;
    }

    return {
      id: BREAK_LIST_DEFAULT_VIEW_ID,
      name: "Default View",
      headerConfig: baseDefaultHeaders.map((header) => ({
        key: header.key,
        name: header.name,
      })),
      headerKeys: baseDefaultHeaders.map((header) => header.key),
      isDefaultView: true,
    };
  }, [baseDefaultHeaders, storedDefaultView]);

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === BREAK_LIST_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      breakColumnHeaders.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: entry.name?.trim() || baseHeader.name,
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = breakColumnHeaders.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders, breakColumnHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(BREAK_LIST_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === BREAK_LIST_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(BREAK_LIST_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(BREAK_LIST_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || BREAK_LIST_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(BREAK_LIST_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={breakColumnHeaders}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={check_access}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === BREAK_LIST_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      check_access,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      breakColumnHeaders,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, topbarCompanyName],
  );

  const newBreakButton = (
    <NewRequestButton
      hasAccess={check_access}
      deniedMessage="Access denied: You are not authorized to create a break request"
      onClick={() => setIsCreatePanelOpen(true)}
    />
  );

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box sx={{ flexGrow: 1, width: "100%", overflow: "visible" }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Mui.Typography sx={designType.pageTitle}>Break</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Review and manage break and cancel break requests awaiting
              approval
            </Mui.Typography>
          </Box>

          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleBreakDateRange}
              includeCurrentDate
              showToday
            />
            {newBreakButton}
          </Mui.Stack>
        </Box>

        <BreakStatusCards
          counts={breakStatusCounts}
          activeFilter={statusCardFilter}
          onCardClick={handleStatusCardClick}
        />

        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            title={"Break Requests"}
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={breakData}
            filteredData={visibleBreakData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            labeledToolbar={true}
          />
        </Box>
        <PendingBulkApprovalActions selection={bulkApproval} />
        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.TableContainer
            ref={containerRef}
            sx={{
              width: "100%",
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
            }}
          >
            <ApproveTable
              stickyHeader
              sx={{
                minWidth: 920,
                width: "100%",
                tableLayout: "fixed",
              }}
            >
              <Mui.TableHead sx={designShell.tableStickyHead}>
                <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  {statusCardFilter === "Pending" && (
                    <Mui.TableCell padding="checkbox" sx={{ ...approveTableHeadCell, width: 48, top: APP_BAR_OFFSET + customFilterHeight }}>
                      {bulkApproval.hasPending && (
                        <Mui.Checkbox
                          size="small"
                          sx={bulkSelectionCheckboxSx}
                          checked={bulkApproval.isAllSelected}
                          indeterminate={bulkApproval.isIndeterminate}
                          disabled={bulkApproval.loading}
                          onChange={bulkApproval.toggleAll}
                          inputProps={{ "aria-label": "Select all pending requests" }}
                        />
                      )}
                    </Mui.TableCell>
                  )}
                  <Mui.TableCell
                    sx={{
                      ...approveTableHeadCell,
                      top: APP_BAR_OFFSET + customFilterHeight,
                      width: 40,
                    }}
                  />
                  {visibleHeaders?.map((value) => (
                    <Mui.TableCell
                      key={value.key}
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      <TruncatedTableValue value={value?.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visibleBreakData?.length === 0 ? (
                  <TableEmptyState
                    dataLength={breakData}
                    colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                  />
                ) : (
                  visibleBreakData
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                    ?.map((value, index) => (
                      <React.Fragment key={value?.id}>
                        <Mui.TableRow
                          onClick={() => {
                            setSelectedData(value);
                            setViewBreakDetails(true);
                          }}
                          sx={{
                            cursor: "pointer",
                            ...designShell.tableRowHover,
                            ...(viewBreakDetails &&
                            value?.id === selectedData?.id
                              ? designShell.neutralSelectedRow
                              : {}),
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          {statusCardFilter === "Pending" && (
                            <Mui.TableCell padding="checkbox" sx={{ ...approveTableBodyCell, width: 48 }} onClick={(event) => event.stopPropagation()}>
                              {statusCardFilter === "Pending" && check_access && canonicalBreakStatus(value.status) === "Pending" && (
                                <Mui.Checkbox
                                  size="small"
                                  sx={bulkSelectionCheckboxSx}
                                  checked={bulkApproval.isSelected(value)}
                                  disabled={bulkApproval.loading}
                                  onChange={() => bulkApproval.toggleRow(value)}
                                  inputProps={{ "aria-label": "Select pending request" }}
                                />
                              )}
                            </Mui.TableCell>
                          )}
                          <Mui.TableCell
                            sx={approveTableBodyCell}
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedIndex((prev) =>
                                prev === index ? null : index,
                              );
                              setExpandedData(value);
                            }}
                          >
                            <ArrowRightIcon
                              sx={{
                                color: designColors.slate500,
                                cursor: "pointer",
                                transition: "transform 0.2s ease",
                                transform:
                                  expandedIndex === index
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                              }}
                            />
                          </Mui.TableCell>
                          {visibleHeaders.map((head) => (
                            <Mui.TableCell
                              key={head.key} data-label={head.name}
                              sx={approveTableBodyCell}
                            >
                              {head.key === "employee_name" ? (
                                <Mui.Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Mui.Avatar
                                    sx={{ width: 28, height: 28 }}
                                    alt={value?.employee_name
                                      ?.charAt(0)
                                      .toUpperCase()}
                                    src={value?.image_url}
                                  />
                                  <Mui.Typography
                                    sx={{
                                      fontWeight:
                                        viewBreakDetails &&
                                        value?.id === selectedData?.id
                                          ? 700
                                          : 500,
                                      color:
                                        viewBreakDetails &&
                                        value?.id === selectedData?.id
                                          ? designColors.slate900
                                          : "inherit",
                                      fontSize: 14,
                                    }}
                                  >
                                    {value?.employee_name || "N/A"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              ) : head.key === "status" ? (
                                <Mui.Box sx={displayFlexItem}>
                                  <StatusBadge
                                    color={
                                      value?.color_code ||
                                      breakStatusVisual(value?.status).accent
                                    }
                                    label={value?.status || "N/A"}
                                  />
                                </Mui.Box>
                              ) : (
                                <TruncatedTableValue
                                  value={value[head.key] ?? "N/A"}
                                />
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>

                        {expandedIndex === index && (
                          <Mui.TableRow>
                            <Mui.TableCell
                              colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                              sx={{
                                p: 2,
                                borderBottom: `1px solid ${designColors.slate200}`,
                              }}
                            >
                              {getStatusFlow(expandedData)[
                                expandedData?.status
                              ]?.map((item, i, arr) => (
                                <StatusRow
                                  key={i}
                                  status={item.status}
                                  user={item.user}
                                  date={item.date}
                                  showConnector={i !== arr.length - 1}
                                />
                              ))}
                              <ApprovalAuditTrail record={expandedData} compact />
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </React.Fragment>
                    ))
                )}
              </Mui.TableBody>
            </ApproveTable>
          </Mui.TableContainer>
          <Box ref={footerRef} sx={approveStickyFooterBar}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleBreakData?.length || 0,
              itemLabel: "break requests",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>
      <Apply
        open={isCreatePanelOpen}
        onClose={() => {
          handleCreateClosePanel();
        }}
        setReCallApi={setReCallApi}
        expanded={createBreakPanelExpanded}
        onToggleExpand={toggleCreateBreakPanelExpanded}
      />
      <BreakDetailPanel
        open={viewBreakDetails}
        onClose={() => setViewBreakDetails(false)}
        breakRequest={selectedData}
        checkAccess={check_access}
        setReCallApi={setReCallApi}
        expanded={breakPanelExpanded}
        onToggleExpand={toggleBreakPanelExpanded}
      />
    </React.Fragment>
  );
};
